// The workspace: sidebar + main column + side panel, driven by the URL. Where the reader is lives
// in the address bar — so back/forward (the arrows in every header, the browser's buttons, Alt+←/→)
// walk the same history, a reload lands in the same place, and a link from the old webapp opens it.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../bridge/client';
import type { SidebarSnapshot } from '../bridge/protocol';
import { ChannelStep } from '../core/channelStep';
import { displayName } from '../core/channelListDerivation';
import { ConnectionBanner, type WebSocketConnectionState } from '../core/connectionBanner';
import type { ChannelRecord, TeamRecord } from '../core/records';
import { TeammateNameDisplay } from '../core/teammateNameDisplay';
import { TypingTracker } from '../core/typingTracker';
import { Icon } from '../design/icons';
import { t, tf } from '../i18n/i18n';
import { Conversation, type JumpAnchor } from './conversation/Conversation';
import { ThreadPane } from './conversation/ThreadPane';
import { ChannelInfoPanel } from './features/channelPanel/ChannelInfoPanel';
import { ActivityView } from './features/destinations/ActivityView';
import { DraftsView } from './features/destinations/DraftsView';
import { SavedView } from './features/destinations/SavedView';
import { ThreadsSheet } from './features/destinations/ThreadsView';
import { BrowseChannelsDialog } from './features/people/BrowseChannelsDialog';
import { CreateChannelDialog } from './features/people/CreateChannelDialog';
import { NewConversationDialog } from './features/people/NewConversationDialog';
import { UserProfileCard } from './features/people/UserProfileCard';
import { SearchView } from './features/search/SearchView';
import { useLive, useLocalStorage } from './hooks';
import { buildSidebar, filterSections } from './models/sidebarModel';
import { prefetchPage } from './pageCache';
import { beginChannelSwitch, count, markFirstMeaningfulPaint } from './perf';
import { ProfileMenu } from './ProfileMenu';
import { SettingsView } from './features/settings/SettingsView';
import { currentRoute, navigate, onRoute, setPermalinkBase, type Destination, type Route } from './router';
import { HistoryArrows, useHistoryKeys } from './shell/HistoryArrows';
import { ShellContext, type Dialog, type Shell, type SidePanel } from './shell/ShellContext';
import { ConversationFace, Sidebar } from './sidebar/Sidebar';
import { AreaPickerDialog } from './features/areas/AreaPickerDialog';
import { AttendanceDialog } from './features/attendance/AttendanceParts';
import { useAttendancePoller } from './features/attendance/attendance';
import { AreaGrouping } from '../core/areaGrouping';
import { useDesktopHeartbeat } from './features/presence/DmPresence';
import { setTwelveHour, twelveHourFromPreference } from '../core/clockStyle';
import { setLinkPreviews } from './conversation/linkPreviews';
import { NotificationPermissionCard, badgeCounts, useTabBadge, useWebNotifications } from './features/notifications/notifications';
import { ExtrasHost } from './features/extras/ExtrasHost';
import { SessionEndRecord } from '../core/sessionEndRecord';
import { ForwardHost } from './features/compose/ForwardDialog';
import { ModerationHost } from './features/moderation/Moderation';
import { CallHost } from './features/calls/CallSurfaces';
import { RemoteSupportHost } from './features/remoteSupport/RemoteSupportSurfaces';
import { WhatsAppChatDialog } from './features/whatsAppChat/WhatsAppChatDialog';
import { DialogHost } from './features/pluginUi/DialogHost';
import { PwaHost } from './features/pwa/PwaHost';
import { UpdateBanner } from './features/pwa/UpdateBanner';
import { DlpBanner } from './features/pwa/DlpBanner';
import { NavHost } from './features/nav/NavHost';
import { LiveRegions } from './a11y/LiveRegions';
import { SkipToMain } from './a11y/SkipToMain';
import { sidebarPacer } from './rebuildPacer';

const SIDEBAR_TABLES = ['teams', 'channels', 'channelMembers', 'categories', 'users', 'userStatuses', 'posts', 'drafts', 'syncState', 'channelParticipants', 'threads'] as const;

const isCompact = () => window.matchMedia('(max-width: 760px)').matches;

/** Per-team last conversation (per browser; a convenience — the account-wide one stays the fallback). */
function lastChannelByTeam(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem('satori.lastChannelByTeam') ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

function rememberLastChannel(teamId: string, channelId: string): void {
  try {
    const map = lastChannelByTeam();
    if (map[teamId] === channelId) return;
    map[teamId] = channelId;
    localStorage.setItem('satori.lastChannelByTeam', JSON.stringify(map));
  } catch {
    /* a per-viewer convenience */
  }
}

type JumpResult = { postId: string; channelId: string; rootId: string; createAt: number } | { error: string };

function recordSessionEnd(reason: 'rejectedByServer' | 'userRequested'): void {
  try {
    SessionEndRecord.record(localStorage, reason, Date.now());
  } catch {
    /* storage blocked: nothing to explain later */
  }
}

export function Workspace({ serverBase, onSignedOut }: { serverBase: string; onSignedOut(): void }) {
  // P111: the sidebar rebuild (worker read + buildSidebar) paces itself by its own cost, and does
  // nothing while the tab is hidden (ui/rebuildPacer.ts).
  const { value: snapshot } = useLive<SidebarSnapshot>(() => data.call('sidebar'), [...SIDEBAR_TABLES], [], { pacer: sidebarPacer });
  useEffect(() => {
    if (snapshot) count('sidebar.snapshot');
  }, [snapshot]);
  const [route, setRoute] = useState<Route>(currentRoute);
  const [query, setQuery] = useState('');
  const [panel, setPanel] = useState<SidePanel>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [jump, setJump] = useState<JumpAnchor | null>(null);
  const [toast, setToast] = useState<{ text: string; tone: 'info' | 'error' } | null>(null);
  const [profileAnchor, setProfileAnchor] = useState<DOMRect | null>(null);
  const [connection, setConnection] = useState<{ state: WebSocketConnectionState; since: number }>(() => ({
    state: (data.lastConnection?.state as WebSocketConnectionState | undefined) ?? { kind: 'connecting' },
    since: Date.now(),
  }));
  const [typingTick, setTypingTick] = useState(0);
  const tracker = useRef(new TypingTracker());
  const [sidebarWidth, setSidebarWidth] = useLocalStorage<string>('satori.sidebarWidth', '296');
  const [panelWidth, setPanelWidth] = useLocalStorage<string>('satori.threadWidth', '420');
  useHistoryKeys();

  useEffect(() => onRoute(setRoute), []);
  useEffect(() => void data.call('composeConfig').then((c) => setPermalinkBase(c.siteUrl)).catch(() => undefined), []);

  // The name policy belongs to the READER, resolved once per snapshot (TeammateNameDisplay).
  useMemo(() => {
    if (!snapshot) return;
    TeammateNameDisplay.setCurrent(
      TeammateNameDisplay.resolveFormat(snapshot.syncState.name_display_config ?? null, snapshot.syncState.name_display_locked ?? null, snapshot.syncState.name_display_preference ?? null),
    );
  }, [snapshot]);
  // The reader's clock and link previews (Configuración › Pantalla), applied app-wide; the key
  // below remounts the main column when either changes, so memoised rows redraw their times.
  const twelveHour = twelveHourFromPreference(snapshot?.syncState.use_military_time);
  const linkPreviews = snapshot?.syncState.link_previews !== 'false';
  setTwelveHour(twelveHour, t('web.clock.am'), t('web.clock.pm'));
  setLinkPreviews(linkPreviews);
  const displayKey = `${twelveHour ? 12 : 24}-${linkPreviews ? 1 : 0}`;

  // MARK: - Resolve the URL against the store

  const team: TeamRecord | null = useMemo(() => {
    if (!snapshot || !snapshot.teams.length) return null;
    return snapshot.teams.find((t) => t.name === route.team) ?? snapshot.teams.find((t) => t.id === snapshot.syncState.last_team_id) ?? snapshot.teams[0];
  }, [snapshot, route.team]);

  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setClock(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const model = useMemo(() => {
    if (!snapshot || !team) return null;
    const startedAt = performance.now();
    const built = buildSidebar(snapshot, team.id, clock);
    const now = performance.now();
    sidebarPacer.add(now - startedAt, now);
    return built;
  }, [snapshot, team, clock]);
  const me = snapshot?.currentUserId ?? '';

  const channelRoute = useCallback(
    (channel: ChannelRecord, thread: string | null = null): Route => {
      const teamName = snapshot?.teams.find((t) => t.id === channel.teamId)?.name ?? team?.name ?? '';
      if (channel.type === 'D') {
        const partnerId = channel.name.split('__').find((id) => id !== me) ?? me;
        const partner = snapshot?.users.find((u) => u.id === partnerId);
        if (partner) return { kind: 'dm', team: teamName, username: partner.username, thread };
      }
      return { kind: 'channel', team: teamName, channelName: channel.name, thread };
    },
    [snapshot, team, me],
  );

  /** The conversation last on screen: Hilos is a SHEET over it (the Mac's `.sheet`), not a page. */
  const lastConversation = useRef<ChannelRecord | null>(null);

  /** The conversation the URL names, or null (not synced yet / unknown). Under the Hilos sheet: the last one. */
  const selected: ChannelRecord | null = useMemo(() => {
    if (!snapshot || !team) return null;
    if (route.kind === 'destination' && route.destination === 'threads') {
      const last = lastConversation.current;
      return last ? snapshot.channels.find((c) => c.id === last.id) ?? null : null;
    }
    if (route.kind === 'channel') {
      return snapshot.channels.find((c) => c.name === route.channelName && (c.teamId === team.id || c.teamId === '')) ?? snapshot.channels.find((c) => c.name === route.channelName) ?? null;
    }
    if (route.kind === 'dm') {
      const user = snapshot.users.find((u) => u.username === route.username);
      if (!user) return null;
      const self = user.id === me;
      return (
        snapshot.channels.find((c) => {
          if (c.type !== 'D') return false;
          const parts = c.name.split('__');
          return self ? parts[0] === me && parts[1] === me : parts.includes(user.id) && parts.includes(me);
        }) ?? null
      );
    }
    return null;
  }, [snapshot, team, route, me]);

  useEffect(() => {
    if (selected && route.kind !== 'destination') lastConversation.current = selected;
    if (selected && team && route.kind !== 'destination') rememberLastChannel(team.id, selected.id);
  }, [selected, route.kind, team]);

  const showToast = useCallback((text: string, tone: 'info' | 'error' = 'info') => {
    setToast({ text, tone });
    setTimeout(() => setToast((cur) => (cur?.text === text ? null : cur)), 3500);
  }, []);

  // A channel that arrived only after ensureChannel/openDirectChannel: open it once it is in the snapshot.
  const pendingOpen = useRef<{ id: string; replace: boolean; thread: string | null } | null>(null);
  useEffect(() => {
    const p = pendingOpen.current;
    if (!p || !snapshot) return;
    const channel = snapshot.channels.find((c) => c.id === p.id);
    if (channel) {
      pendingOpen.current = null;
      navigate(channelRoute(channel, p.thread), { replace: p.replace });
    }
  }, [snapshot, channelRoute]);

  const goToChannel = useCallback(
    (channelId: string, opts: { replace?: boolean; thread?: string | null } = {}) => {
      const channel = snapshot?.channels.find((c) => c.id === channelId);
      if (channel) navigate(channelRoute(channel, opts.thread ?? null), { replace: opts.replace });
      else {
        pendingOpen.current = { id: channelId, replace: !!opts.replace, thread: opts.thread ?? null };
        void data.call('ensureChannel', channelId).then((ok) => {
          if (!ok) showToast(t('mac.permalink.unavailable'), 'error');
        });
      }
    },
    [snapshot, channelRoute, showToast],
  );

  const jumpToPost = useCallback(
    async (postId: string, replace = false) => {
      const target = (await data.call('resolveJump', postId)) as JumpResult;
      if ('error' in target) {
        showToast(t('mac.permalink.unavailable'), 'error');
        if (replace && team) navigate({ kind: 'home', team: team.name }, { replace: true });
        return;
      }
      const pivotId = target.rootId || target.postId;
      await data.call('loadAround', target.channelId, pivotId);
      let createAt = target.createAt;
      if (target.rootId) {
        const root = (await data.call('resolveJump', target.rootId)) as JumpResult;
        if (!('error' in root)) createAt = root.createAt;
      }
      // A reply lives in its thread: the conversation opens on the ROOT, the thread beside it.
      setJump({ channelId: target.channelId, postId: pivotId, createAt, token: Date.now() });
      goToChannel(target.channelId, { replace, thread: target.rootId || null });
    },
    [team, goToChannel, showToast],
  );

  // Corrections that REPLACE the entry (never a new history step): no team in the URL, home, a
  // permalink resolved to its conversation, a DM that has to be created first.
  const resolving = useRef('');
  useEffect(() => {
    if (!snapshot || !team) return;
    if (route.kind === 'home') {
      if (isCompact()) {
        if (route.team !== team.name) navigate({ kind: 'home', team: team.name }, { replace: true });
        return;
      }
      // The team's OWN last conversation (P271 "remembers last team/channel"): switching teams must
      // not bounce back to another team's channel through the account-wide last one.
      const wanted = lastChannelByTeam()[team.id] ?? snapshot.syncState.last_channel_id;
      const last = snapshot.channels.find((c) => c.id === wanted && !c.deleteAt && (c.teamId === team.id || c.teamId === ''));
      if (last) navigate(channelRoute(last), { replace: true });
      else if (route.team !== team.name) navigate({ kind: 'home', team: team.name }, { replace: true });
      return;
    }
    if (route.kind === 'permalink' && resolving.current !== route.postId) {
      resolving.current = route.postId;
      void jumpToPost(route.postId, true);
      return;
    }
    if (route.kind === 'dm' && !selected) {
      const key = 'dm:' + route.username;
      if (resolving.current === key) return;
      resolving.current = key;
      void (async () => {
        let userId = snapshot.users.find((u) => u.username === route.username)?.id;
        if (!userId) userId = ((await data.call('resolveUserByUsername', route.username).catch(() => null)) as { id: string } | null)?.id;
        if (!userId) return showToast(t('mac.permalink.unavailable'), 'error');
        const r = (await data.callUntyped<{ channelId?: string }>('openDirectChannel', [userId]).catch(() => null));
        if (!r?.channelId) showToast(t('channel.action.failed'), 'error');
      })();
    }
  }, [snapshot, team, route, selected, channelRoute, jumpToPost, showToast]);

  useEffect(() => {
    if (team) void data.call('setSyncValues', { last_team_id: team.id });
  }, [team?.id]);

  // Per conversation: the PRE-clear viewed-at (the unread separator) and where the reader came from.
  const lastSelected = useRef<string | null>(null);
  const boundaries = useRef(new Map<string, number | null>());
  const previousChannel = useRef<string | null>(null);
  if (selected && lastSelected.current !== selected.id) {
    previousChannel.current = lastSelected.current;
    lastSelected.current = selected.id;
    const member = snapshot?.members.find((m) => m.channelId === selected.id);
    boundaries.current.set(selected.id, member && selected.lastPostAt > member.lastViewedAt ? member.lastViewedAt : null);
  }

  useEffect(() => {
    if (model && (model.sections.length || model.firstRun)) markFirstMeaningfulPaint(`${model.sections.reduce((n, s) => n + s.rows.length, 0)} rows`);
  }, [model]);

  // MARK: - Signals (typing, connection, session)

  useEffect(() => {
    const off = data.onSignal((s) => {
      if (s.kind === 'typing') {
        if (s.parentId.startsWith('__clear__')) tracker.current.clear(s.userId, s.channelId, s.parentId.slice(9));
        else tracker.current.note(s.userId, s.channelId, s.parentId, Date.now());
        setTypingTick((n) => n + 1);
      } else if (s.kind === 'connection') {
        setConnection({ state: s.state as WebSocketConnectionState, since: Date.now() });
      } else if (s.kind === 'authExpired') {
        // The login screen will explain a sign-out the reader did not ask for (PARITY P310).
        recordSessionEnd('rejectedByServer');
        onSignedOut();
      }
    });
    if (data.lastConnection) setConnection({ state: data.lastConnection.state as WebSocketConnectionState, since: Date.now() });
    return off;
  }, [onSignedOut]);
  useEffect(() => {
    const id = setInterval(() => {
      if (tracker.current.prune(Date.now())) setTypingTick((n) => n + 1);
    }, 2000);
    return () => clearInterval(id);
  }, []);
  const [, setBannerTick] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setBannerTick((n) => n + 1), ConnectionBanner.graceMillis + 50);
    return () => clearTimeout(id);
  }, [connection]);
  useEffect(() => {
    const onVisible = () => document.visibilityState === 'visible' && void data.call('foreground');
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // MARK: - Shell actions (everything turns into a URL change)

  const openChannel = useCallback(
    (channelId: string) => {
      if (channelId === selected?.id && route.kind !== 'destination') return;
      beginChannelSwitch(channelId);
      // Ask the worker for the page NOW, before React re-renders anything.
      void prefetchPage(channelId);
      setJump(null);
      goToChannel(channelId);
    },
    [selected?.id, route.kind, goToChannel],
  );

  const openThread = useCallback(
    (rootId: string) => {
      setPanel({ kind: 'thread', rootId });
      if (route.kind === 'channel' || route.kind === 'dm' || route.kind === 'destination') navigate({ ...route, thread: rootId });
    },
    [route],
  );

  const openDestination = useCallback(
    (destination: Destination, param: string | null = null) => {
      if (team) navigate({ kind: 'destination', team: team.name, destination, param });
    },
    [team],
  );

  const shell: Shell | null = useMemo(() => {
    if (!team || !model?.me) return null;
    return {
      team,
      me: model.me,
      teams: snapshot?.teams ?? [],
      openChannel,
      openThread,
      openThreadInConversation: (channelId, rootId) => {
        beginChannelSwitch(channelId);
        void prefetchPage(channelId);
        setJump(null);
        goToChannel(channelId, { thread: rootId });
      },
      jumpToPost: (postId) => void jumpToPost(postId),
      openSidePanel: (p) => setPanel(p),
      openDestination: (d) => openDestination(d),
      openSearch: (q) => {
        if (route.kind === 'destination' && route.destination === 'search') navigate({ kind: 'destination', team: team.name, destination: 'search', param: q }, { replace: true });
        else openDestination('search', q);
      },
      openDialog: setDialog,
      openUser: (target, anchor) => setDialog({ kind: 'userProfile', ...target, anchor }),
      openDirectMessage: (userId) =>
        void (async () => {
          const r = (await data.callUntyped<{ channelId?: string }>('openDirectChannel', [userId]).catch(() => null));
          if (!r?.channelId) return showToast(t('channel.action.failed'), 'error');
          setDialog(null);
          openChannel(r.channelId);
        })(),
      toast: showToast,
      openSettings: (section) => openDestination('settings', section),
      openHome: () => navigate({ kind: 'home', team: team.name }),
    };
  }, [team, model?.me, snapshot?.teams, openChannel, openThread, goToChannel, jumpToPost, openDestination, route, showToast]);

  // The info panel belongs to the conversation on screen: switching conversations carries it along.
  useEffect(() => {
    setPanel((p) => (p?.kind === 'channelInfo' && selected && p.channelId !== selected.id ? { ...p, channelId: selected.id } : p));
  }, [selected?.id]);

  // A conversation the reader just left, archived or closed vanishes from the store: go back to the
  // list rather than keep an empty, titleless pane on screen.
  const seenSelected = useRef<string | null>(null);
  useEffect(() => {
    if (selected) {
      seenSelected.current = selected.id;
      return;
    }
    const gone = seenSelected.current;
    if (!gone || !snapshot || !team) return;
    const still = snapshot.channels.find((c) => c.id === gone);
    if (!still && (route.kind === 'channel' || route.kind === 'dm')) {
      seenSelected.current = null;
      setPanel(null);
      navigate({ kind: 'home', team: team.name }, { replace: true });
    }
  }, [selected, snapshot, team, route.kind]);

  // The thread panel follows the URL's ?thread= (so back closes it).
  useEffect(() => {
    const thread = route.kind === 'channel' || route.kind === 'dm' || route.kind === 'destination' ? route.thread ?? null : null;
    setPanel((p) => {
      if (thread) return p?.kind === 'thread' && p.rootId === thread ? p : { kind: 'thread', rootId: thread };
      return p?.kind === 'thread' ? null : p;
    });
  }, [route]);

  const closePanel = useCallback(() => {
    if (panel?.kind === 'thread' && (route.kind === 'channel' || route.kind === 'dm' || route.kind === 'destination') && route.thread) navigate({ ...route, thread: null });
    setPanel(null);
  }, [panel, route]);

  // ⌥↑ / ⌥↓: the conversation above / below, over the rows the sidebar is DRAWING (also Ctrl+Tab
  // where the browser delivers it — features/extras/keyboard.ts).
  const stepConversation = useCallback(
    (direction: 'previous' | 'next') => {
      if (!model) return;
      const sections = filterSections(model.sections, query, false);
      const next = ChannelStep.target(sections, query.trim() !== '', selected?.id ?? null, direction);
      if (next) {
        openChannel(next);
        document.querySelector(`[data-channel-id="${next}"]`)?.scrollIntoView({ block: 'nearest' });
      }
    },
    [model, query, selected?.id, openChannel],
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') || !model) return;
      e.preventDefault();
      stepConversation(e.key === 'ArrowUp' ? 'previous' : 'next');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [model, stepConversation]);

  const selectTeam = useCallback(
    (id: string) => {
      const next = snapshot?.teams.find((t) => t.id === id);
      if (next) navigate({ kind: 'home', team: next.name });
    },
    [snapshot?.teams],
  );

  // MARK: - Derived view state

  const row = useMemo(() => model?.sections.flatMap((s) => s.rows).find((r) => r.id === selected?.id) ?? null, [model, selected?.id]);
  let subtitle = '';
  if (selected?.type === 'D' && row?.dmPartnerUsername) subtitle = '@' + row.dmPartnerUsername;
  else if (selected?.header) subtitle = selected.header;
  let readOnly: string | null = null;
  if (selected?.deleteAt) readOnly = t('channel.archived.readOnly');
  else if (row?.inactive === 'deactivatedPartner') readOnly = tf('channel.deactivatedPartner.readOnly', row.dmPartnerName ?? '');
  // §44: a read-only rule on the server (only administrators may post here).
  else if (selected && (snapshot?.syncState.readonly_channel_ids ?? '').split(',').includes(selected.id)) readOnly = t('channel.readOnlyByRule');

  const banner = ConnectionBanner.decide(connection.state, Date.now() - connection.since);
  const strip =
    banner.kind === 'none' ? null : banner.kind === 'connecting' ? t('channel.status.connecting') : ConnectionBanner.showsAttempt(banner) ? tf('channel.status.reconnectingAttempt', banner.attempt) : t('channel.status.reconnecting');
  void typingTick;
  const typists = selected ? tracker.current.typistIds(selected.id, '', Date.now()) : [];

  const startResize = (which: 'sidebar' | 'panel') => (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const start = Number(which === 'sidebar' ? sidebarWidth : panelWidth);
    const move = (ev: PointerEvent) => {
      const delta = ev.clientX - startX;
      if (which === 'sidebar') setSidebarWidth(String(Math.max(240, Math.min(420, start + delta))));
      else setPanelWidth(String(Math.max(320, Math.min(640, start - delta))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const destination = route.kind === 'destination' ? route.destination : null;
  useWebNotifications({
    visibleChannelId: destination ? null : selected?.id ?? null,
    onOpen: (n) => void jumpToPost(n.postId),
    teamNameFor: (teamId) => snapshot?.teams.find((x) => x.id === teamId)?.name ?? team?.name ?? '',
  });
  useTabBadge(snapshot ?? null, destination ? null : row?.name ?? null);
  // Others see this person on a computer only if the tab says so (shared/PLUGINS.md §8.1).
  useDesktopHeartbeat(!!me);
  useAttendancePoller();
  const showsMain = !!selected || !!destination;
  const backToList = () => team && navigate({ kind: 'home', team: team.name });
  const headerLeading = (
    <>
      <button className="icon-button compact-back" onClick={backToList} title={t('web.nav.backToList')} aria-label={t('web.nav.backToList')}>
        <Icon name="chevronLeft" size={18} strokeWidth={2} />
      </button>
      <HistoryArrows />
    </>
  );

  const signOut = async () => {
    recordSessionEnd('userRequested');
    await data.call('logout');
    onSignedOut();
  };

  let main = null;
  // Hilos is drawn as a sheet over the conversation (below), never in place of it.
  if (destination && destination !== 'threads' && shell) {
    main =
      destination === 'settings' ? (
        <SettingsView section={route.kind === 'destination' ? route.param ?? null : null} onSignOut={signOut} />
      ) : destination === 'activity' ? (
        <ActivityView />
      ) : destination === 'saved' ? (
        <SavedView />
      ) : destination === 'drafts' ? (
        <DraftsView />
      ) : (
        <SearchView query={route.kind === 'destination' ? route.param ?? '' : ''} />
      );
  } else if (selected) {
    main = (
      <Conversation
        key={selected.id}
        channelId={selected.id}
        previousChannelId={previousChannel.current}
        title={
          row?.name ??
          displayName(selected, {
            currentUserId: me,
            usersById: model?.usersById ?? new Map(),
            participantIds: snapshot?.participants[selected.id] ?? [],
            unknownUserFallback: selected.type === 'G' ? t('channelList.unknownGroup') : t('channelList.unknownUser'),
          })
        }
        subtitle={subtitle}
        icon={selected.type === 'P' ? 'lock' : selected.type === 'O' ? (AreaGrouping.isAreaChannelName(selected.name) ? 'area' : 'hash') : selected.type === 'G' ? 'group' : 'dm'}
        unreadBoundary={boundaries.current.get(selected.id) ?? null}
        me={me}
        myUsername={model?.me?.username ?? null}
        teamName={team?.name ?? ''}
        typists={typists}
        readOnlyNotice={readOnly}
        archived={!!selected?.deleteAt}
        onOpenThread={openThread}
        onOpenUser={(username, anchor) => setDialog({ kind: 'userProfile', username, anchor })}
        onOpenUserId={(userId, anchor) => setDialog({ kind: 'userProfile', userId, anchor })}
        onOpenInfo={(tab) => setPanel({ kind: 'channelInfo', channelId: selected.id, tab })}
        onSearchHere={() => openDestination('search', selected.type === 'D' && row?.dmPartnerUsername ? `in:@${row.dmPartnerUsername} ` : `in:${selected.name} `)}
        onToast={showToast}
        jump={jump && jump.channelId === selected.id ? jump : null}
        onLeaveJump={() => setJump(null)}
        connectionStrip={strip}
        headerLeading={headerLeading}
        headerFace={row && (row.type === 'D' || row.type === 'G') ? <ConversationFace row={row} /> : undefined}
        dmPartner={
          selected.type === 'D' && row?.dmPartnerId && row.dmPartnerUsername
            ? { userId: row.dmPartnerId, username: row.dmPartnerUsername, noPhrase: row.dmPartnerId === me }
            : null
        }
      />
    );
  }

  return (
    <ShellContext.Provider value={shell}>
      <div className={`workspace${showsMain ? ' has-selection' : ''}`} style={{ ['--sidebar-width' as string]: `${sidebarWidth}px` }}>
        <SkipToMain />
        <Sidebar
          model={model}
          teamId={team?.id ?? null}
          selectedChannelId={destination ? null : selected?.id ?? null}
          activeDestination={destination}
          onSelectTeam={selectTeam}
          onSelectChannel={openChannel}
          onOpenDestination={(d) => openDestination(d)}
          onSearchMessages={(q) => openDestination('search', q)}
          onNew={(kind) => setDialog({ kind })}
          onToggleCategory={(id, collapsed) => void data.call('setCategoryCollapsedSynced', id, collapsed)}
          onMarkRead={(id) => void data.call('markChannelRead', id)}
          onOpenProfile={(rect) => setProfileAnchor(rect)}
          query={query}
          onQuery={setQuery}
          serverBase={serverBase}
          draftCount={model?.draftCount ?? 0}
          notice={
            <>
              <DlpBanner />
              <UpdateBanner />
              <NotificationPermissionCard />
            </>
          }
        />
        <div className="splitter" onPointerDown={startResize('sidebar')} role="separator" aria-orientation="vertical" />
        {/* The main landmark (P255). display: contents — the layout is the workspace's own grid. */}
        <main className="workspace-main" aria-label={t('web.a11y.mainRegion')}>
        {main ? <Fragment key={displayKey}>{main}</Fragment> : (
          <section className="conversation-pane">
            <div className="wallpaper" />
            <div className="empty-state">
              <h2>{t('mac.noConversationSelected')}</h2>
              <p>{t('mac.noConversationHint')}</p>
            </div>
          </section>
        )}
        </main>
        {panel && shell ? (
          <>
            <div className="splitter" style={{ marginLeft: -4 }} onPointerDown={startResize('panel')} role="separator" aria-orientation="vertical" />
            {panel.kind === 'thread' ? (
              <ThreadPane
                rootId={panel.rootId}
                me={me}
                myUsername={model?.me?.username ?? null}
                teamName={team?.name ?? ''}
                onClose={closePanel}
                onOpenUser={(username, anchor) => setDialog({ kind: 'userProfile', username, anchor })}
                onOpenUserId={(userId, anchor) => setDialog({ kind: 'userProfile', userId, anchor })}
                onToast={showToast}
                width={Number(panelWidth)}
              />
            ) : (
              <ChannelInfoPanel channelId={panel.channelId} tab={panel.tab} width={Number(panelWidth)} onClose={closePanel} />
            )}
          </>
        ) : null}
        {shell && dialog?.kind === 'newConversation' ? <NewConversationDialog onClose={() => setDialog(null)} /> : null}
        {shell && dialog?.kind === 'browseChannels' ? <BrowseChannelsDialog onClose={() => setDialog(null)} /> : null}
        {shell && dialog?.kind === 'createChannel' ? <CreateChannelDialog onClose={() => setDialog(null)} /> : null}
        {shell && dialog?.kind === 'messageArea' ? <AreaPickerDialog onClose={() => setDialog(null)} /> : null}
        {shell && dialog?.kind === 'attendance' ? <AttendanceDialog view={dialog.view} onClose={() => setDialog(null)} /> : null}
        {shell && dialog?.kind === 'whatsAppChat' ? <WhatsAppChatDialog selectedChannelId={selected?.id ?? null} onClose={() => setDialog(null)} /> : null}
        {shell && destination === 'threads' ? (
          <ThreadsSheet
            focusRootId={route.kind === 'destination' ? route.param ?? null : null}
            onClose={() => {
              // Back to the conversation the sheet was over (or the team's home when there was none).
              const last = lastConversation.current;
              if (last) goToChannel(last.id);
              else if (team) navigate({ kind: 'home', team: team.name });
            }}
          />
        ) : null}
        {shell ? <DialogHost /> : null}
        <LiveRegions />
        {shell ? (
          <NavHost
            model={model}
            teamId={team?.id ?? null}
            currentChannelId={destination ? null : selected?.id ?? null}
            onSelectTeam={selectTeam}
            onOpenChannel={openChannel}
          />
        ) : null}
        {shell && dialog?.kind === 'userProfile' ? <UserProfileCard userId={dialog.userId} username={dialog.username} anchor={dialog.anchor} onClose={() => setDialog(null)} /> : null}
        {shell ? <ForwardHost /> : null}
        {shell ? <ModerationHost /> : null}
        {shell ? <CallHost me={me || null} serverBase={serverBase} channelId={destination ? null : selected?.id ?? null} channelName={row?.name ?? ''} /> : null}
        {shell ? <RemoteSupportHost me={me || null} /> : null}
        {shell ? <PwaHost selectedChannelId={destination ? null : selected?.id ?? null} modalOpen={!!dialog} /> : null}
        {shell ? <ExtrasHost mentions={badgeCounts(snapshot ?? null).mentions} stepConversation={stepConversation} /> : null}
        {profileAnchor && model?.me ? (
          <ProfileMenu
            anchor={profileAnchor}
            me={model.me}
            presence={model.myPresence}
            onClose={() => setProfileAnchor(null)}
            onSignOut={signOut}
            onOpenSettings={(section) => openDestination('settings', section)}
          />
        ) : null}
        {toast ? (
          <div className={`toast${toast.tone === 'error' ? ' error' : ''}`} role="status">
            {toast.text}
          </div>
        ) : null}
      </div>
    </ShellContext.Provider>
  );
}
